import {CSSProperties, useEffect, useMemo, useRef, useState} from "react";

// Image preview with a magenta pivot crosshair and click-to-set-pivot.
//
// Pivot values are Unity sprite pivots: NORMALIZED 0..1 with the origin at
// the BOTTOM-LEFT (Y up) — the client passes them straight into
// Sprite.Create. Browser coordinates are Y-down, so both the line placement
// and the click mapping invert Y.
//
// The fiddly part is knowing the rendered image rect. Rather than measuring
// the img after CSS layout (max-width/max-height of an auto-sized parent is
// unreliable to overlay), we compute the contain-fit rect ourselves from the
// observed container size + the image's natural size, and position the img
// AND the crosshair from the same numbers — they can't disagree.
interface PivotPreviewProps {
    src: string;
    alt: string;
    // Crosshair renders only when both are provided.
    pivotX?: number;
    pivotY?: number;
    // When set, clicking or dragging on the image picks a pivot and the
    // cursor becomes a crosshair. The crosshair tracks the pointer live
    // during a drag, but onPickPivot fires ONCE on release (rounded to
    // 3 decimals, clamped 0..1) — one command/undo step per gesture.
    onPickPivot?: (x: number, y: number) => void;
    // Live drag feedback: fires with the in-flight pivot on every move and
    // with undefined when the gesture ends (committed or cancelled). Lets
    // hosts preview the pivot elsewhere (e.g. the Workshop) without
    // committing commands mid-drag.
    onDragPivot?: (pivot: {x: number; y: number} | undefined) => void;
    // Optional annotation box, drawn as a dashed outline: the subject bounds the
    // client crops to when framing this art as a UI icon. Same convention as the
    // pivot and as the stored value — NORMALIZED 0..1, origin BOTTOM-LEFT — so the
    // Y flip happens here, once, alongside the crosshair's.
    subjectBounds?: {x: number; y: number; w: number; h: number};
    // 'contain': fill the container, scale to fit (upscales small images so
    //            pivot clicks have precision). 'natural': true pixel size,
    //            container shrink-wraps the image (fullscreen overlay).
    fit: 'contain' | 'natural';
    style?: CSSProperties;        // container styling
    imageStyle?: CSSProperties;   // applied to the img (e.g. checkerboard)
}

const LINE_COLOR = 'magenta';
const BOUNDS_COLOR = '#31d1ff';

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

function PivotPreview(props: PivotPreviewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState<{w: number; h: number}>({w: 0, h: 0});
    const [naturalSize, setNaturalSize] = useState<{w: number; h: number} | undefined>(undefined);

    // Forget the previous image's dimensions the moment the source changes —
    // otherwise the old rect briefly positions the new image.
    useEffect(() => {
        setNaturalSize(undefined);
    }, [props.src]);

    useEffect(() => {
        if (props.fit !== 'contain') return;
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => {
            const r = entries[0].contentRect;
            setContainerSize({w: r.width, h: r.height});
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [props.fit]);

    // The rendered image rect, in container coordinates.
    const rect = useMemo(() => {
        if (!naturalSize || naturalSize.w <= 0 || naturalSize.h <= 0) return undefined;
        if (props.fit === 'natural') {
            return {left: 0, top: 0, width: naturalSize.w, height: naturalSize.h};
        }
        if (containerSize.w <= 0 || containerSize.h <= 0) return undefined;
        const scale = Math.min(containerSize.w / naturalSize.w, containerSize.h / naturalSize.h);
        const width = naturalSize.w * scale;
        const height = naturalSize.h * scale;
        return {
            left: (containerSize.w - width) / 2,
            top: (containerSize.h - height) / 2,
            width,
            height,
        };
    }, [naturalSize, containerSize, props.fit]);

    // In-flight drag pivot (full precision for smooth line tracking);
    // undefined when not dragging.
    const [drag, setDrag] = useState<{x: number; y: number} | undefined>(undefined);

    function pivotFromEvent(e: React.PointerEvent<HTMLImageElement>) {
        const r = e.currentTarget.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) return undefined;
        return {
            x: clamp01((e.clientX - r.left) / r.width),
            y: clamp01(1 - (e.clientY - r.top) / r.height),   // Y up, per Unity
        };
    }

    function onPointerDown(e: React.PointerEvent<HTMLImageElement>) {
        if (!props.onPickPivot || !rect) return;
        // Picking consumes the gesture; read-only previews let clicks bubble
        // so hosts (e.g. the fullscreen overlay) can treat them as dismiss.
        e.stopPropagation();
        const p = pivotFromEvent(e);
        if (!p) return;
        // Capture so the drag keeps tracking (clamped) outside the image.
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag(p);
    }

    function onPointerMove(e: React.PointerEvent<HTMLImageElement>) {
        if (!drag) return;
        const p = pivotFromEvent(e);
        if (!p) return;
        setDrag(p);
        props.onDragPivot?.(p);
    }

    function onPointerUp(e: React.PointerEvent<HTMLImageElement>) {
        if (!drag || !props.onPickPivot) return;
        const p = pivotFromEvent(e) ?? drag;
        setDrag(undefined);
        props.onDragPivot?.(undefined);
        const round3 = (v: number) => clamp01(Math.round(v * 1000) / 1000);
        props.onPickPivot(round3(p.x), round3(p.y));
    }

    function onPointerCancel() {
        if (!drag) return;
        setDrag(undefined);
        props.onDragPivot?.(undefined);
    }

    const shownPivot = drag
        ?? (props.pivotX !== undefined && props.pivotY !== undefined
            ? {x: props.pivotX, y: props.pivotY}
            : undefined);
    const showCrosshair = rect !== undefined && shownPivot !== undefined;
    // Keep the 1px lines inside the image even at pivot 0/1.
    const lineX = rect && shownPivot
        ? rect.left + Math.min(rect.width - 1, clamp01(shownPivot.x) * rect.width) : 0;
    const lineY = rect && shownPivot
        ? rect.top + Math.min(rect.height - 1, (1 - clamp01(shownPivot.y)) * rect.height) : 0;

    const imgStyle: CSSProperties = props.fit === 'natural'
        ? {display: 'block', imageRendering: 'pixelated'}
        : rect
            ? {
                position: 'absolute',
                left: rect.left, top: rect.top,
                width: rect.width, height: rect.height,
                display: 'block', imageRendering: 'pixelated',
            }
            // Mounted-but-unmeasured: keep it invisible (onLoad still fires).
            : {position: 'absolute', opacity: 0, maxWidth: '100%', maxHeight: '100%'};

    return (
        <div ref={containerRef}
             style={{position: 'relative', overflow: 'hidden', ...props.style}}>
            <img alt={props.alt} src={props.src}
                 onLoad={e => setNaturalSize({
                     w: e.currentTarget.naturalWidth,
                     h: e.currentTarget.naturalHeight,
                 })}
                 onPointerDown={onPointerDown}
                 onPointerMove={onPointerMove}
                 onPointerUp={onPointerUp}
                 onPointerCancel={onPointerCancel}
                 draggable={false}
                 style={{
                     ...imgStyle,
                     cursor: props.onPickPivot ? 'crosshair' : undefined,
                     touchAction: 'none', userSelect: 'none',
                     ...props.imageStyle,
                 }}/>
            {rect && props.subjectBounds && (
                <div style={{
                    position: 'absolute', pointerEvents: 'none',
                    left: rect.left + props.subjectBounds.x * rect.width,
                    // Y up in the data, Y down on screen: the box's TOP edge is its
                    // far edge from the origin, hence 1 - (y + h).
                    top: rect.top + (1 - (props.subjectBounds.y + props.subjectBounds.h)) * rect.height,
                    width: props.subjectBounds.w * rect.width,
                    height: props.subjectBounds.h * rect.height,
                    border: `1px dashed ${BOUNDS_COLOR}`,
                    boxSizing: 'border-box',
                }}/>
            )}
            {showCrosshair && rect && <>
                <div style={{
                    position: 'absolute', pointerEvents: 'none',
                    left: lineX, top: rect.top, width: 1, height: rect.height,
                    backgroundColor: LINE_COLOR,
                }}/>
                <div style={{
                    position: 'absolute', pointerEvents: 'none',
                    left: rect.left, top: lineY, width: rect.width, height: 1,
                    backgroundColor: LINE_COLOR,
                }}/>
            </>}
        </div>
    );
}

export default PivotPreview;
